<script>
import { mapActions } from 'vuex';
import {
  closeModal,
  iButton,
  iButtonStyles,
  iButtonTypes,
  iDropzone,
  iIcon,
  iModal,
  openModal,
  showToast,
} from '@inscribe/signal';

export default {
  components: { iButton, iDropzone, iIcon, iModal },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      files: [],
      isLoading: false,
      iButtonStyles,
      iButtonTypes,
    };
  },

  computed: {
    hasValidFiles() {
      return this.files.length > 0;
    },
  },
  watch: {
    visible: {
      handler: function (modalVisible) {
        if (modalVisible) {
          openModal('upload-database-modal');
        }
      },
    },
  },
  mounted() {},
  methods: {
    ...mapActions(['addBlocklistings']),
    onClose() {
      this.$emit('on-close');
    },
    closeModal() {
      closeModal('upload-database-modal');
    },
    onFilesSelected(selected) {
      Array.from(selected).forEach((selectedFile) => {
        this.files.push(selectedFile);
      });
    },
    deleteFile(file) {
      const foundIndex = this.files.findIndex((f) => f === file);
      if (foundIndex > -1) {
        this.files.splice(foundIndex, 1);
      }
    },
    uploadDatabase() {
      this.isLoading = true;
      const data = new FormData();
      for (const file of this.files) {
        const fileUUID = crypto.randomUUID();
        data.append(file.name, file);
        data.append(`${file.name}_uuid`, fileUUID);
      }
      this.files = [];
      this.addBlocklistings(data)
        .then((response) => {
          for (const entry of response.data.data) {
            this.$store.commit('addBlocklist', entry);
          }
          this.closeModal();
          showToast({ text: 'Blocklist added' });
        })
        .catch(() => {
          showToast({
            text: `Could not add blocklist, please try again later.`,
            isError: true,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template>
  <div class="blocklist-upload">
    <i-modal
      ref="myModal"
      modal-id="upload-database-modal"
      title="Upload Database"
      subtitle="Add a file with blocklist details"
      :close-on-click-outside="false"
      :close-on-submit="false"
      :confirm-button-loading="isLoading"
      :confirm-button-disabled="isLoading || !hasValidFiles"
      :cancel-button-disabled="isLoading"
      :close-button-disabled="isLoading"
      :submit-on-enter="true"
      confirm-button-text="Upload"
      @on-submit="uploadDatabase()"
      @on-close="onClose"
    >
      <template #body-content>
        <div
          class="d-flex flex-column i-gap-10"
          data-testid="new-customer-modal"
        >
          <!-- info box -->
          <div
            class="info-box d-flex flex-row align-items-center i-px-8 i-py-6 i-radius-xl i-gap-6"
          >
            <div class="icon-wrapper">
              <i-icon
                symbol="small-solid-info"
                class="i-text-neutral-100"
                :size="16"
              ></i-icon>
            </div>
            <div class="text i-text-subheading-2 i-text-neutral-120">
              Create and upload a CSV or Excel file with columns for "Name" and
              "Address"
            </div>
          </div>

          <!-- files list -->
          <div
            v-if="files.length > 0"
            class="files-list d-flex flex-column i-gap-2"
          >
            <div
              v-for="(file, index) in Array.from(files)"
              :key="index"
              class="file-item d-flex flex-row align-items-center i-radius-sm i-px-6 i-py-5 i-text-subheading-2 i-text-neutral-120"
            >
              <i-icon
                symbol="small-file"
                :size="16"
                class="file-icon i-mr-6"
              ></i-icon>
              <div class="name flex-grow-1">{{ file.name }}</div>
              <i-button
                :left-icon="'small-delete-1'"
                :type="iButtonTypes.NEUTRAL"
                :button-style="iButtonStyles.GHOST"
                :disabled="isLoading"
                @button-click="deleteFile(file)"
              ></i-button>
            </div>
          </div>

          <i-dropzone
            v-if="!hasValidFiles"
            title="Choose file or drag and drop"
            sub-title="CSV or Excel files accepted"
            :accepted-extensions="['csv', 'xls', 'xlsx']"
            :disabled="isLoading"
            :multi-file="false"
            @files-selected="onFilesSelected"
          ></i-dropzone>
        </div>
      </template>
    </i-modal>
  </div>
</template>

<style lang="scss">
.modal-content {
  .files-list {
    max-height: 200px;
    overflow-y: auto;

    .file-item {
      background: $light-neutral-20;
    }
  }

  .info-box {
    border: 1px solid $light-neutral-60;
  }
}
</style>
